import { DownArrow } from "../svg";
import MinusButton from "@/components/minus-button";
import Checkbox from "../checkbox";
import classNames from "classnames";
import {
  ITEM_CATEGORIES,
  MIN_ITEM_DESCRIPTION_LENGTH,
  MAX_ITEM_DESCRIPTION_LENGTH,
} from "@/lib/constants";

export const ITEM_DESC_ERROR_MSG = `${MIN_ITEM_DESCRIPTION_LENGTH}-${MAX_ITEM_DESCRIPTION_LENGTH} characters required. Vague entries risk delays.`;

export default function Item({
  itemIndex,
  packageIndex,
  itemObj,
  parcel,
  onQuantityChange,
  onDescriptionChange,
  onCategoryChange,
  onTotalValueChange,
  onTotalWeightChange,
  onFragileChange,
  onRemoveItem,
  onValidityChange,
  packingType = null,
  showWeight,
  deviceTypePrefix,
  disabled = false,
}) {
  const itemGridColsClass = showWeight
    ? "grid-cols-[38px_auto_18%_15%_15.5%]"
    : "grid-cols-[38px_auto_25%_20%]";

  return (
    <div className="flex flex-col my-2">
      <div
        className={classNames(
          "rounded-xl group border border-border-dark-gray shadow-border",
          `grid ${itemGridColsClass} gap-x-1.5 relative h-[36px] w-full`
        )}
      >
        <label htmlFor="quantity" hidden>
          quantity
        </label>
        <input
          type="text"
          inputMode="numeric"
          id={`${deviceTypePrefix}-quantity-p${packageIndex}i${itemIndex}`}
          className={`focus:outline-none px-1 border-2 border-transparent placeholder:text-placeholder-gray ring-[1.5px] focus:ring-light-purple rounded-xl ${
            itemObj.validQuantity !== "none" && !itemObj.validQuantity
              ? " ring-error-red"
              : "ring-transparent"
          }`}
          placeholder="8"
          value={itemObj.quantity || ""}
          onBlur={(event) => {
            onValidityChange("validQuantity", !!event.target.value);
          }}
          onChange={(event) => onQuantityChange(event.target.value)}
          disabled={disabled}
        />
        <div
          className={`${
            itemObj.validDescription !== "none" && !itemObj.validDescription
              ? "ring-error-red"
              : "ring-transparent"
          } flex items-center relative ring-[1.5px] focus-within:ring-light-purple rounded-xl group`}
        >
          <label htmlFor="description" hidden>
            item description
          </label>
          <input
            type="text"
            id={`${deviceTypePrefix}-description-p${packageIndex}i${itemIndex}`}
            className={`focus:outline-none placeholder:text-placeholder-gray px-1 h-full rounded-xl w-full`}
            placeholder="Used men's cotton T-shirts, size L, blue"
            value={itemObj.description || ""}
            maxLength={MAX_ITEM_DESCRIPTION_LENGTH}
            onBlur={(event) => {
              onValidityChange(
                "validDescription",
                event.target.value.length >= MIN_ITEM_DESCRIPTION_LENGTH &&
                  event.target.value.length <= MAX_ITEM_DESCRIPTION_LENGTH
              );
            }}
            onChange={(event) => onDescriptionChange(event.target.value)}
            disabled={disabled}
          />
        </div>
        <div
          className={`flex flex-wrap items-center rounded-xl ring-[1.5px] focus-within:ring-light-purple relative ${
            !itemObj.validCategory ? "ring-error-red" : "ring-transparent"
          }`}
        >
          <select
            id={`${deviceTypePrefix}-category-p${packageIndex}i${itemIndex}`}
            className={`focus:outline-none appearance-none bg-transparent w-full h-full rounded-xl mx-0.5 ${
              !itemObj.category ? "text-placeholder-gray" : ""
            }`}
            value={itemObj.category || "none"}
            onBlur={(event) => {
              onValidityChange(
                "validCategory",
                !!event.target.value && event.target.value !== "none"
              );
            }}
            onChange={(event) => onCategoryChange(event.target.value)}
            disabled={disabled}
          >
            <option value="none" className="disabled:bg-none" disabled>
              Category
            </option>
            {ITEM_CATEGORIES.map((category) => (
              <option
                key={category.value}
                value={category.value}
                className="text-black"
              >
                {category.text}
              </option>
            ))}
          </select>
          {!itemObj.category ? (
            <DownArrow
              color="#844FFA"
              className="absolute right-3 w-[12px] h-[8px] pointer-events-none"
            />
          ) : null}
        </div>
        <label htmlFor="total value" hidden>
          item total value
        </label>
        <div
          className={`px-1 flex items-center border-transparent ring-[1.5px] focus-within:ring-light-purple rounded-xl ${
            itemObj.validTotalValue !== "none" && !itemObj.validTotalValue
              ? "ring-error-red"
              : "ring-transparent"
          }`}
        >
          <p className="text-dark-purple">S$</p>
          <input
            type="text"
            inputMode="decimal"
            id={`${deviceTypePrefix}-total-value-p${packageIndex}i${itemIndex}`}
            className="focus:outline-none placeholder:text-placeholder-gray rounded-xl w-full h-full px-1"
            placeholder="60"
            value={itemObj.totalValue || ""}
            onChange={(event) => onTotalValueChange(event.target.value)}
            disabled={disabled}
          />
        </div>
        {showWeight && (
          <>
            <label htmlFor="total weight" hidden>
              item total weight
            </label>
            <div
              className={`flex items-center border-transparent justify-end pr-2 ring-[1.5px] focus-within:ring-light-purple rounded-xl ${
                !itemObj.validTotalWeight && itemObj.validTotalWeight !== "none"
                  ? "ring-error-red"
                  : "ring-transparent"
              }`}
            >
              <input
                type="text"
                inputMode="decimal"
                id={`${deviceTypePrefix}-total-weight-p${packageIndex}i${itemIndex}`}
                className="focus:outline-none text-right placeholder:text-placeholder-gray rounded-xl w-[60%] h-full px-1"
                placeholder="4.0"
                value={itemObj.totalWeight || ""}
                onChange={(event) => onTotalWeightChange(event.target.value)}
                disabled={disabled}
              />
              <p className="text-dark-purple">kg</p>
            </div>
          </>
        )}
        {parcel.items.length > 1 ? (
          <MinusButton
            className="absolute -right-2.5 -top-2.5 hover:cursor-pointer hidden group-hover:flex"
            onClick={onRemoveItem}
          />
        ) : null}
      </div>
      <div className="flex mb-6 pl-12 w-full select-none justify-between">
        <p
          className={`h-1 text-xs ${
            itemObj.validDescription === false ? "text-red-500" : "invisible"
          }`}
        >
          {ITEM_DESC_ERROR_MSG}
        </p>

        {packingType === "Fragile" ? (
          <>
            <Checkbox
              id={`${deviceTypePrefix}-fragile-item-p${packageIndex}i${itemIndex}`}
              label="Fragile"
              containerClassName="flex items-center mt-1 justify-self-end"
              checked={itemObj.isFragile}
              onChange={() => onFragileChange(!itemObj.isFragile)}
              className="w-[15px] h-[15px] mr-1"
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
